<!-- 玄学机构端（商户台）：高端服务管理——服务配置（定价/阶段定稿/上下架）+ 高端订单（派单/进度）+ 机构钱包 -->
<template>
  <ContentWrap>
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <!-- ==================== Tab 1：服务配置 ==================== -->
      <el-tab-pane label="服务配置" name="config">
        <el-table v-loading="configLoading" :data="configs">
          <el-table-column label="服务名称" align="center" prop="catalogName" min-width="130" show-overflow-tooltip />
          <el-table-column label="分类" align="center" width="95">
            <template #default="{ row }">
              <el-tag type="warning" effect="plain">{{ PREMIUM_CATEGORY_LABELS[row.category] || row.category }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="平台基准价" align="center" width="105">
            <template #default="{ row }">
              <span class="text-gray-400 line-through">¥{{ yuan(row.basePrice) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="本机构挂牌价" align="center" width="110">
            <template #default="{ row }">
              <span v-if="row.price" class="font-semibold">¥{{ yuan(row.price) }}</span>
              <span v-else class="text-gray-400">未配置</span>
            </template>
          </el-table-column>
          <el-table-column label="可调区间" align="center" width="150">
            <template #default="{ row }">¥{{ yuan(row.priceMin) }} ~ ¥{{ yuan(row.priceMax) }}</template>
          </el-table-column>
          <el-table-column label="阶段" align="center" width="80">
            <template #default="{ row }">
              {{ (row.stages || row.templateStages || []).length }} 个
            </template>
          </el-table-column>
          <el-table-column label="状态" align="center" width="100">
            <template #default="{ row }">
              <el-switch
                v-model="row.status"
                :active-value="0"
                :inactive-value="1"
                :disabled="!row.configId"
                @change="handleConfigStatus(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center" width="90" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" @click="openConfigForm(row)">
                {{ row.configId ? '编辑' : '上架' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- ==================== Tab 2：高端订单（派单） ==================== -->
      <el-tab-pane label="高端订单" name="orders">
        <el-form :inline="true" class="-mb-15px">
          <el-form-item label="状态">
            <el-select v-model="orderQuery.status" placeholder="全部" clearable class="!w-140px" @change="loadOrders">
              <el-option v-for="(label, key) in PREMIUM_ORDER_STATUS_LABELS" :key="key" :label="label" :value="Number(key)" />
            </el-select>
          </el-form-item>
        </el-form>
        <el-table v-loading="orderLoading" :data="orders">
          <el-table-column label="订单号" align="center" prop="orderNo" min-width="170" show-overflow-tooltip />
          <el-table-column label="服务" align="center" prop="catalogName" min-width="120" show-overflow-tooltip />
          <el-table-column label="金额" align="center" width="100">
            <template #default="{ row }">¥{{ yuan(row.price) }}</template>
          </el-table-column>
          <el-table-column label="状态" align="center" width="90">
            <template #default="{ row }">
              <el-tag :type="row.bizStatus === 0 ? 'warning' : row.bizStatus === 1 ? 'primary' : 'success'" effect="plain">
                {{ PREMIUM_ORDER_STATUS_LABELS[row.bizStatus] || '—' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="服务师傅" align="center" width="100">
            <template #default="{ row }">{{ row.masterName || '—' }}</template>
          </el-table-column>
          <el-table-column label="进度" align="center" width="130">
            <template #default="{ row }">
              <el-progress
                v-if="row.bizStatus === 1"
                :percentage="row.progress || 0"
                :stroke-width="10"
              />
              <span v-else-if="row.bizStatus === 2" class="text-green-600">已交付</span>
              <span v-else class="text-gray-400">—</span>
            </template>
          </el-table-column>
          <el-table-column label="下单时间" align="center" prop="createTime" width="170" :formatter="dateFormatter" />
          <el-table-column label="操作" align="center" width="150" fixed="right">
            <template #default="{ row }">
              <el-button
                v-if="row.bizStatus === 0 && row.tradeStatus === 20"
                link
                type="warning"
                @click="openAssign(row)"
              >
                派单
              </el-button>
              <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>
        <Pagination
          v-model:limit="orderQuery.pageSize"
          v-model:page="orderQuery.pageNo"
          :total="orderTotal"
          @pagination="loadOrders"
        />
      </el-tab-pane>

      <!-- ==================== Tab 3：机构钱包 ==================== -->
      <el-tab-pane label="机构钱包" name="wallet">
        <el-row :gutter="16" class="mb-16px">
          <el-col :span="8">
            <el-card shadow="never">
              <el-statistic title="可提现余额（元）" :value="walletBalanceYuan" :precision="2" />
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card shadow="never">
              <el-statistic title="累计收入（元）" :value="walletIncomeYuan" :precision="2" />
            </el-card>
          </el-col>
        </el-row>
        <el-table v-loading="flowLoading" :data="flows">
          <el-table-column label="关联订单号" align="center" prop="orderNo" min-width="170">
            <template #default="{ row }">{{ row.orderNo || '—' }}</template>
          </el-table-column>
          <el-table-column label="类型" align="center" width="100">
            <template #default="{ row }">
              <el-tag :type="row.amount > 0 ? 'success' : 'danger'" effect="plain">
                {{ FLOW_TYPE_LABELS[row.type] || '—' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="金额" align="center" width="120">
            <template #default="{ row }">
              <span :class="row.amount > 0 ? 'text-green-600' : 'text-red-500'">
                {{ row.amount > 0 ? '+' : '' }}¥{{ yuan(row.amount) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="变动后余额" align="center" width="120">
            <template #default="{ row }">¥{{ yuan(row.balanceAfter) }}</template>
          </el-table-column>
          <el-table-column label="备注" align="left" prop="remark" min-width="180" show-overflow-tooltip />
          <el-table-column label="时间" align="center" prop="createTime" width="170" :formatter="dateFormatter" />
        </el-table>
      </el-tab-pane>
    </el-tabs>
  </ContentWrap>

  <!-- 服务配置弹窗（定价 + 阶段定稿） -->
  <el-dialog v-model="configVisible" :title="configForm.configId ? '编辑高端服务' : '上架高端服务'" width="640px">
    <el-alert
      type="info"
      :closable="false"
      class="mb-12px"
      :title="`「${configForm.catalogName}」平台基准价 ¥${yuan(configForm.basePrice)}，挂牌价须在 ¥${yuan(configForm.priceMin)} ~ ¥${yuan(configForm.priceMax)} 区间内`"
    />
    <el-form ref="configFormRef" :model="configForm" label-width="90px">
      <el-form-item label="挂牌价" required>
        <el-input-number v-model="configForm.priceYuan" :min="1" :precision="2" :step="100" class="!w-200px" />
        <span class="ml-8px text-gray-400 text-12px">元，用户端成交价</span>
      </el-form-item>
      <el-form-item label="服务说明">
        <el-input
          v-model="configForm.intro"
          type="textarea"
          :rows="3"
          maxlength="500"
          placeholder="覆盖平台默认说明（可空）"
        />
      </el-form-item>
      <el-form-item label="阶段定稿" required>
        <div class="w-full">
          <div v-for="(s, i) in configForm.stages" :key="i" class="mb-8px flex items-center gap-8px">
            <el-input v-model="s.name" maxlength="50" placeholder="阶段名称" class="!w-180px" />
            <el-input v-model="s.description" maxlength="255" placeholder="阶段说明" class="flex-1" />
            <el-button link type="danger" @click="configForm.stages!.splice(i, 1)">删除</el-button>
          </div>
          <el-button link type="primary" @click="configForm.stages!.push({ name: '', description: '', sort: configForm.stages!.length })">
            <Icon icon="ep:plus" class="mr-4px" /> 添加阶段
          </el-button>
          <div class="mt-4px text-gray-400 text-12px">基于平台模板自定义；派单后阶段快照进订单，师傅端只读</div>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="configVisible = false">取消</el-button>
      <el-button type="primary" :loading="configSaving" @click="submitConfig">确定</el-button>
    </template>
  </el-dialog>

  <!-- 派单弹窗 -->
  <el-dialog v-model="assignVisible" title="派单" width="480px">
    <el-form label-width="90px">
      <el-form-item label="服务">
        <span>{{ assignRow?.catalogName }}（¥{{ yuan(assignRow?.price || 0) }}）</span>
      </el-form-item>
      <el-form-item label="选择师傅" required>
        <el-select v-model="assignMasterId" placeholder="选择本机构已挂靠师傅" class="!w-full">
          <el-option v-for="m in affiliations" :key="m.masterId" :label="m.masterName" :value="m.masterId" />
        </el-select>
        <div class="mt-4px text-gray-400 text-12px">
          派单后阶段定稿快照进订单（师傅端只读），订单进入服务中；师傅逐阶段上传报告，全部上传完自动完结结算
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="assignVisible = false">取消</el-button>
      <el-button type="primary" :loading="assigning" @click="submitAssign">确认派单</el-button>
    </template>
  </el-dialog>

  <!-- 订单详情弹窗（阶段进度） -->
  <el-dialog v-model="detailVisible" title="高端订单详情" width="620px">
    <el-descriptions :column="2" border size="small" class="mb-12px">
      <el-descriptions-item label="订单号">{{ detail?.orderNo }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ PREMIUM_ORDER_STATUS_LABELS[detail?.bizStatus as number] || '—' }}</el-descriptions-item>
      <el-descriptions-item label="服务">{{ detail?.catalogName }}</el-descriptions-item>
      <el-descriptions-item label="金额">¥{{ yuan(detail?.price || 0) }}</el-descriptions-item>
      <el-descriptions-item label="服务师傅">{{ detail?.masterName || '—' }}</el-descriptions-item>
      <el-descriptions-item label="进度">{{ detail?.progress || 0 }}%</el-descriptions-item>
    </el-descriptions>
    <div v-if="detail?.userRemark" class="mb-12px text-13px">
      <span class="text-gray-500">用户备注：</span>{{ detail.userRemark }}
    </div>
    <el-timeline>
      <el-timeline-item
        v-for="s in detail?.stages || []"
        :key="s.index"
        :type="s.done ? 'success' : 'primary'"
        :hollow="!s.done"
      >
        <div class="font-semibold">{{ s.name }}</div>
        <div v-if="s.description" class="text-gray-500 text-12px">{{ s.description }}</div>
        <div v-if="s.done" class="text-12px">
          <a :href="s.reportUrl" target="_blank" class="text-blue-500">查看报告（{{ s.reportFileName }}）</a>
        </div>
      </el-timeline-item>
    </el-timeline>
  </el-dialog>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import {
  PremiumMerchantApi,
  PremiumCatalogApi,
  PREMIUM_CATEGORY_LABELS,
  PREMIUM_ORDER_STATUS_LABELS,
  type InstitutionPremiumConfigVo,
  type PremiumOrderVo
} from '@/api/xuanxue/premium'
import { AffiliationApi, type AffiliationVo } from '@/api/xuanxue/institution'
import type { FormInstance } from 'element-plus'

defineOptions({ name: 'XuanxuePremiumMerchant' })

const message = useMessage()

/** 分 → 元展示（整数省小数） */
const yuan = (fen?: number) => {
  if (!fen) return '0'
  const v = fen / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

const FLOW_TYPE_LABELS: Record<number, string> = { 1: '订单收入', 2: '提现支出', 3: '调整' }

const activeTab = ref('config')

// ========== 服务配置 ==========
const configLoading = ref(false)
const configs = ref<InstitutionPremiumConfigVo[]>([])

const loadConfigs = async () => {
  configLoading.value = true
  try {
    configs.value = await PremiumMerchantApi.getMyConfigs()
  } finally {
    configLoading.value = false
  }
}

const configVisible = ref(false)
const configSaving = ref(false)
const configFormRef = ref<FormInstance>()

interface ConfigFormVo {
  configId?: number
  catalogId: number
  catalogName: string
  basePrice: number
  priceMin: number
  priceMax: number
  priceYuan?: number
  intro?: string
  status?: number
  stages?: { name: string; description?: string; sort?: number }[]
}

const configForm = ref<ConfigFormVo>({ catalogId: 0, catalogName: '', basePrice: 0, priceMin: 0, priceMax: 0 })

const openConfigForm = (row: InstitutionPremiumConfigVo) => {
  configForm.value = {
    configId: row.configId,
    catalogId: row.catalogId,
    catalogName: row.catalogName,
    basePrice: row.basePrice,
    priceMin: row.priceMin,
    priceMax: row.priceMax,
    priceYuan: row.price ? row.price / 100 : undefined,
    intro: row.intro,
    status: 0,
    stages: (row.stages || row.templateStages || []).map((s, i) => ({ ...s, sort: i }))
  }
  configVisible.value = true
}

const submitConfig = async () => {
  const f = configForm.value
  if (!f.priceYuan) {
    message.error('请填写挂牌价')
    return
  }
  const price = Math.round(f.priceYuan * 100)
  if (price < f.priceMin || price > f.priceMax) {
    message.error(`挂牌价须在 ¥${yuan(f.priceMin)} ~ ¥${yuan(f.priceMax)} 区间内`)
    return
  }
  if (!f.stages || f.stages.length === 0 || f.stages.some((s) => !s.name || !s.name.trim())) {
    message.error('请至少配置一个阶段任务，且阶段名称不能为空')
    return
  }
  configSaving.value = true
  try {
    await PremiumMerchantApi.saveConfig({
      configId: f.configId,
      catalogId: f.catalogId,
      price,
      intro: f.intro,
      status: f.status,
      stages: f.stages.map((s, i) => ({ name: s.name.trim(), description: s.description, sort: i }))
    })
    message.success('保存成功')
    configVisible.value = false
    await loadConfigs()
  } finally {
    configSaving.value = false
  }
}

const handleConfigStatus = async (row: InstitutionPremiumConfigVo) => {
  try {
    await message.confirm(`确认${row.status === 0 ? '上架' : '下架'}「${row.catalogName}」？`)
    await PremiumMerchantApi.updateConfigStatus(row.configId!, row.status)
    message.success(row.status === 0 ? '已上架' : '已下架')
  } catch {
    await loadConfigs()
  }
}

// ========== 高端订单（派单） ==========
const orderLoading = ref(false)
const orders = ref<PremiumOrderVo[]>([])
const orderTotal = ref(0)
const orderQuery = reactive({ pageNo: 1, pageSize: 10, status: undefined as number | undefined })

const loadOrders = async () => {
  orderLoading.value = true
  try {
    const data = await PremiumMerchantApi.getOrderPage(orderQuery)
    orders.value = data.list
    orderTotal.value = data.total
  } finally {
    orderLoading.value = false
  }
}

// 派单
const assignVisible = ref(false)
const assigning = ref(false)
const assignRow = ref<PremiumOrderVo | null>(null)
const assignMasterId = ref<number>()
const affiliations = ref<AffiliationVo[]>([])

const openAssign = async (row: PremiumOrderVo) => {
  assignRow.value = row
  assignMasterId.value = undefined
  // 本机构已挂靠师傅
  const data = await AffiliationApi.getPage({ pageNo: 1, pageSize: 100, status: 1 })
  affiliations.value = data.list || []
  if (!affiliations.value.length) {
    message.warning('本机构暂无已挂靠师傅，请先在「旗下师傅」中审核挂靠申请')
    return
  }
  assignVisible.value = true
}

const submitAssign = async () => {
  if (!assignMasterId.value) {
    message.error('请选择服务师傅')
    return
  }
  assigning.value = true
  try {
    await PremiumMerchantApi.assign({ tradeOrderId: assignRow.value!.id, masterId: assignMasterId.value })
    message.success('派单成功，订单进入服务中')
    assignVisible.value = false
    await loadOrders()
  } finally {
    assigning.value = false
  }
}

// 详情
const detailVisible = ref(false)
const detail = ref<PremiumOrderVo | null>(null)

const openDetail = async (row: PremiumOrderVo) => {
  detail.value = await PremiumMerchantApi.getOrder(row.id)
  detailVisible.value = true
}

// ========== 机构钱包 ==========
const flowLoading = ref(false)
const walletBalanceYuan = ref(0)
const walletIncomeYuan = ref(0)
const flows = ref<Record<string, any>[]>([])

const loadWallet = async () => {
  flowLoading.value = true
  try {
    const [wallet, flowList] = await Promise.all([
      PremiumMerchantApi.getWallet(),
      PremiumMerchantApi.getWalletFlows(50)
    ])
    walletBalanceYuan.value = (wallet.balance || 0) / 100
    walletIncomeYuan.value = (wallet.totalIncome || 0) / 100
    flows.value = flowList || []
  } finally {
    flowLoading.value = false
  }
}

/** tab 切换懒加载 */
const handleTabChange = (name: string | number) => {
  if (name === 'config') loadConfigs()
  else if (name === 'orders') loadOrders()
  else if (name === 'wallet') loadWallet()
}

/** 初始化（含平台高端目录刷新缓存提示） */
onMounted(() => {
  loadConfigs()
})
</script>
